import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar as CalendarIcon, Mail, User, MessageSquare, Loader2, ExternalLink } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import SEOSchema from '../components/SEOSchema';

// Use relative path for production compatibility
const API = '/api';

// Track event helper
const trackEvent = (eventType, extra = {}) => {
  axios.post(`${API}/stats/track`, { event_type: eventType, ...extra }).catch(() => {});
};

// CANONICAL URL - Production domain
const CANONICAL_DOMAIN = 'https://renaissancegardendubrovnik.com';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [dateRange, setDateRange] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(true);

  // Track page visit
  useEffect(() => {
    trackEvent('visit', { page: 'contact' });
  }, []);

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const response = await axios.get(`${API}/availability`);
        if (response.data.success) {
          const dates = [];
          response.data.booked_dates.forEach(booking => {
            const start = parseISO(booking.start);
            const end = parseISO(booking.end);
            
            const current = new Date(start);
            while (current <= end) {
              dates.push(new Date(current));
              current.setDate(current.getDate() + 1);
            }
          });
          setBookedDates(dates);
        }
      } catch (error) {
        console.error('Error fetching availability:', error);
        toast.error('Could not load availability calendar');
      } finally {
        setIsLoadingAvailability(false);
      }
    };

    fetchAvailability();
  }, []);

  const isDateBooked = (date) => {
    return bookedDates.some(
      bookedDate =>
        bookedDate.getDate() === date.getDate() &&
        bookedDate.getMonth() === date.getMonth() &&
        bookedDate.getFullYear() === date.getFullYear()
    );
  };

  const disableDate = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today || isDateBooked(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dateRange?.from || !dateRange?.to) {
      toast.error('Please select both check-in and check-out dates');
      return;
    }

    if (dateRange.to <= dateRange.from) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        check_in: format(dateRange.from, 'yyyy-MM-dd'),
        check_out: format(dateRange.to, 'yyyy-MM-dd'),
        message: formData.message,
      };

      await axios.post(`${API}/leads`, payload);

      toast.success('Your inquiry has been sent! We will get back to you soon.');

      setFormData({ name: '', email: '', message: '' });
      setDateRange(undefined);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20 stone-texture min-h-screen" data-testid="contact-page">
      {/* Page-specific SEO via SEOSchema component */}
      <SEOSchema page="contact" />

      <section className="relative h-80 flex items-center justify-center" data-testid="contact-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://a0.muscache.com/im/pictures/hosting/Hosting-36517649/original/c440d638-647d-44f4-8227-6c61dd20f2cc.jpeg?im_w=1200')",
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl">
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold drop-shadow-lg" data-testid="contact-title">
            Book Your Stay
          </h1>
          <p className="text-lg mt-4 drop-shadow-md">Choose your preferred booking method</p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Option A: Direct Inquiry */}
            <Card className="border-2 border-primary" data-testid="direct-booking-card">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="font-playfair text-2xl font-bold text-primary mb-2">Option A: Direct Inquiry</h3>
                  <p className="text-muted-foreground">Get personalized service and special offers</p>
                </div>
                <div className="bg-primary/5 rounded-sm p-4 mb-6">
                  <p className="text-sm text-center font-medium">✓ Direct communication with hosts</p>
                  <p className="text-sm text-center font-medium">✓ Flexible booking terms</p>
                  <p className="text-sm text-center font-medium">✓ Special requests accommodated</p>
                </div>
              </CardContent>
            </Card>

            {/* Option B: Airbnb */}
            <Card className="border-2 border-secondary" data-testid="airbnb-booking-card">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h3 className="font-playfair text-2xl font-bold text-secondary mb-2">Option B: Book via Airbnb</h3>
                  <p className="text-muted-foreground">Trusted platform with instant confirmation</p>
                </div>
                <div className="bg-secondary/5 rounded-sm p-4 mb-6">
                  <p className="text-sm text-center font-medium">✓ Superhost Verified Listing</p>
                  <p className="text-sm text-center font-medium">✓ 50+ Five-Star Reviews</p>
                  <p className="text-sm text-center font-medium">✓ Secure payment & booking protection</p>
                </div>
                <a
                  href="https://www.airbnb.com/rooms/36517649?source_impression_id=p3_1774275773_P36zG1iQyJKkzDXq"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid="airbnb-booking-link"
                >
                  <Button className="w-full bg-secondary hover:bg-secondary/90 text-white py-6 text-lg rounded-sm transition-all hover:scale-105 font-playfair">
                    Prefer Airbnb? Book via our Verified Listing
                    <ExternalLink className="ml-2" size={20} />
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>

          {/* Direct Inquiry Form */}
          <div className="bg-white rounded-none shadow-xl p-8 md:p-12 border border-border" data-testid="contact-form-container">
            <h2 className="font-playfair text-3xl font-bold text-center mb-2" data-testid="form-heading">
              Send Us a Direct Inquiry
            </h2>
            <p className="text-center text-muted-foreground mb-8" data-testid="form-description">
              Fill out the form below and we'll get back to you as soon as possible
            </p>

            {isLoadingAvailability && (
              <div className="text-center mb-4 text-muted-foreground" data-testid="loading-availability">
                <Loader2 className="inline h-4 w-4 animate-spin mr-2" />
                Loading availability...
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div data-testid="form-field-name">
                <Label htmlFor="name" className="flex items-center gap-2 mb-2">
                  <User size={16} />
                  Your Name *
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Enter your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="input-name"
                />
              </div>

              <div data-testid="form-field-email">
                <Label htmlFor="email" className="flex items-center gap-2 mb-2">
                  <Mail size={16} />
                  Email Address *
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your.email@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="rounded-sm"
                  data-testid="input-email"
                />
              </div>

              <div data-testid="form-field-dates">
                <Label className="flex items-center gap-2 mb-2">
                  <CalendarIcon size={16} />
                  Select Your Dates *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal rounded-sm"
                      data-testid="date-range-button"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {dateRange?.from ? (
                        dateRange.to ? (
                          <>
                            {format(dateRange.from, 'PPP')} - {format(dateRange.to, 'PPP')}
                          </>
                        ) : (
                          format(dateRange.from, 'PPP')
                        )
                      ) : (
                        'Select check-in and check-out dates'
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start" data-testid="date-range-popover">
                    <Calendar
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      disabled={disableDate}
                      initialFocus
                      numberOfMonths={2}
                      modifiers={{
                        booked: bookedDates
                      }}
                      modifiersStyles={{
                        booked: {
                          textDecoration: 'line-through',
                          opacity: 0.4,
                          backgroundColor: '#e5e5e5'
                        }
                      }}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {bookedDates.length > 0 && (
                <div className="text-sm text-muted-foreground text-center" data-testid="booked-dates-info">
                  <span className="inline-block w-3 h-3 bg-gray-300 rounded-sm mr-1"></span>
                  Greyed out dates are already booked
                </div>
              )}

              <div data-testid="form-field-message">
                <Label htmlFor="message" className="flex items-center gap-2 mb-2">
                  <MessageSquare size={16} />
                  Message *
                </Label>
                <Textarea
                  id="message"
                  placeholder="Tell us about your stay... any special requests?"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="rounded-sm"
                  data-testid="input-message"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-primary/90 text-white py-6 text-lg rounded-sm transition-all hover:scale-105"
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Direct Inquiry'
                )}
              </Button>
            </form>
          </div>

          <div className="mt-16 text-center" data-testid="contact-info">
            <h3 className="font-playfair text-2xl font-semibold mb-4">Hosts: Slavica & Marijo</h3>
            <p className="text-muted-foreground">
              We look forward to welcoming you to Villa Renaissance Garden!
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;